import React, {
  useCallback, useContext, useEffect, useRef, useState, useMemo
} from 'react';
import { string, bool, number, shape } from 'prop-types';
import {
  bool as boolType,
  extend,
  hoist,
  params,
  QueryContext,
  QueryProvider,
  shape as shapeType,
  string as stringType,
  number as numberType
} from '@thd-nucleus/data-sources';
import {
  ExperienceContext,
  useStore,
  useConfigService
} from '@thd-nucleus/experience-context';
import { debounce } from '@thd-olt-functional/utils';
import { BrowseSearchRedirector, DynamicComponent } from '@thd-nucleus/app-render';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { CategoryCardsDesktop } from '@thd-olt-component-react/category-cards';
import { Col, Row } from '@thd-olt-component-react/core-ui';
import { EmtQuestionsAndAnswersImporter } from '@thd-olt-component-react/emt-generated-content';
import { EtchWidgets } from '@thd-olt-component-react/etch-widgets';
import { HdhomeSignup } from '@thd-olt-component-react/email-signup';
import { declareContexts, Hydrator } from '@thd-olt-component-react/hydrator';
import { HybridContentfulWidgets, useHybridPLP } from '@thd-olt-component-react/hybrid';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { useHelmet } from '@thd-nucleus/thd-helmet';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import {
  RectShape,
  TextBlock,
  LoadingPlaceholder
} from '@thd-olt-component-react/loading-placeholder';
import { BrowseSearchMetadata, Metadata } from '@thd-olt-component-react/metadata';
import { NoResultsFound } from '@thd-olt-component-react/no-results-found';
import { ProductPodPlaceholder } from '@thd-olt-component-react/product-pod';
import { CompareTray } from '@thd-olt-component-react/compare';
import {
  CustomProductPodDetails,
  ProductPodGroup
} from '@thd-olt-component-react/product-pod-group';
import {
  parseUrl,
  ProductResults,
  ProductResultsContext,
  productResultsDataModel,
  ResultsApplied,
  ResultsDimensions,
  ResultsFilterAndSortDrawer,
  ResultsHeader,
  ResultsPagination,
  ResultsSortBy,
  ResultsWrapped,
  updateQueryParams,
  storeLifecycleEvent,
  useProductResults
} from '@thd-olt-component-react/product-results';
import { RelatedSearch } from '@thd-olt-component-react/related-search';
import {
  DiscoveryZonesWrapper,
  PlpRecsRenderPlaceholder,
  PlpRvRenderPlaceholder,
  BuyItAgain
} from '@thd-olt-component-react/thd-recs-containers';
import { useNavigate, useLocation } from '@thd-olt-component-react/router';
import { SearchFeedback } from '@thd-olt-component-react/search-feedback';
import { VisualCategoryNavigation } from '@thd-olt-component-react/visual-navigation';
import { useThdCustomer, CustomerContext } from '@thd-olt-functional/customer-information';
import {
  SponsoredBanner,
  SponsoredInGridBanner,
  SponsoredSkyscraperBanner
} from '@thd-olt-component-react/sponsored-content';
import { RetailMediaCarousel } from '@thd-olt-component-react/retail-media-pla-sponsored-placements';
import { OrderSample, usePLPSamples } from '@thd-olt-component-react/order-sample';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { CustomProductPodResponsive } from './CustomProductPodResponsive';
import {
  EmtGeneratedContent,
  EmtGeneratedContentLoad,
  EmtGeneratedContentModel,
  EmtLinks,
  EmtLinksLoad,
  EmtLinksModel
} from './dynamic-imports';
import {
  getBrowseAndSearchItemIds,
  shouldDisplayResultsAppliedInner,
  shouldDisplayResultsAppliedOuter,
  impProductPodData,
  impPLPRecs,
  imagePreloader,
  isRefinedPage,
  isNIncluded
} from './browse-and-search-utils';
import { hasInStoreFilter } from '../../utils';
import { browseCampaignId, subscriptionType, requestKey } from '../../constants';
import '../../styles/browse-search.scss';

const HoistedProductPodGroup = hoist(
  ProductPodGroup,
  CategoryCardsDesktop,
  CustomProductPodResponsive,
  CustomProductPodDetails
);

export const BrowseAndSearch = (props) => {
  declareContexts([ExperienceContext, QueryContext, ProductResultsContext, CustomerContext]);
  const HYDRATION_STANDARD_SCROLL_BUFFER = 1500;
  const CAROUSEL_HYDRATION_STANDARD_SCROLL_BUFFER = 2500;

  // Discovery Zones state management for dynamic placement and rendering
  const [discoveryZoneContainerIndex, setDiscoveryZoneContainerIndex] = useState({
    zone1: 8,
    zone2: 16
  });
  const [isDiscoveryZonesLoaded, setIsDiscoveryZonesLoaded] = useState(false);
  const [inGridBannerIndex, setInGridBannerIndex] = useState(null);
  const [isInGridBannerBottom, setIsInGridBannerBottom] = useState(false);

  const navigate = useNavigate();
  const ctx = useContext(ExperienceContext);
  const {
    clientStore, cookie, deliveryZip: serverDeliveryZip, segment
  } = ctx;
  const { search: querystring } = useLocation();
  const {
    pageType,
    productPodHoverDelay,
    pathname,
    customerType,
    instance
  } = props;
  const isSearch = pageType === 'search';
  const isBrowse = pageType === 'browse';
  const path = `${pathname}${querystring}`;
  const disableSSR = typeof window !== 'undefined' && window.DISABLE_SSR;
  const varnish = !disableSSR && !isSearch;
  const store = useStore({ varnish, online: false });
  const isNoResultsFoundRef = useRef(false);
  const SERVER_SIDE_PRODUCT_POD_COUNT = 12;
  const CLIENT_SIDE_PRODUCT_POD_COUNT = 48;
  const searchNavPropsRef = useRef({
    askForSponsored: isSearch,
    deliveryZip: serverDeliveryZip,
    pageSize: disableSSR ? CLIENT_SIDE_PRODUCT_POD_COUNT : SERVER_SIDE_PRODUCT_POD_COUNT
  });

  const isDiscoveryZonesEnabled = useConfigService('fs-prop:isDiscoveryZonesEnabled');
  const isDiscoveryZonesSearchEnabled = useConfigService('fs-prop:isDiscoveryZonesSearchEnabled');
  const viewAllFilterButton = useConfigService('fs-prop:isViewAllFilterButtonEnabled');
  const viewPrimaryFilters = useConfigService('fs-prop:isviewPrimaryFiltersEnabled');
  const isFulfillmentRedesignEnabled = useConfigService('fs-prop:isFulfillmentRedesignEnabled');
  const viewVizNavFilters = useConfigService('fs-prop:isViewVizNavFiltersEnabled');
  const isGetItFastOnPlp = useConfigService('fs-prop:isGetItFastOnPlp');
  const isInGridBannerEnabled = useConfigService('fs-prop:isInGridBannerEnabled') || false;
  const filterBrowseRecs = useConfigService('fs:browseFilteredRecs');
  const isFulfillmentFSEnabled = useConfigService('fs:isFulfillmentFilterAndMsgEnabled') || false;
  // Combine these flags into a single condition we can check more easily.
  const isDiscoveryZonesEligible = (isBrowse && isDiscoveryZonesEnabled) || (isSearch && isDiscoveryZonesSearchEnabled);

  let podIndex = -1;

  let THDCustomer = {};
  const thdCustomer = useThdCustomer();
  const { isCustomerIdentified = false } = useThdCustomer();

  const theme = useTheme(BrowseAndSearch);
  const { hideFFMFilters, hideEmailPromo, hideFeedback } = theme.props;

  if (clientStore) {
    let deliveryZipCode;
    if (
      typeof window !== 'undefined'
      && window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails
    ) {
      deliveryZipCode = window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails();
    }
    searchNavPropsRef.current = {
      pageSize: CLIENT_SIDE_PRODUCT_POD_COUNT,
      askForSponsored: true,
      deliveryZip: deliveryZipCode?.zipcode
    };
    THDCustomer = typeof window !== 'undefined' ? window.THDCustomer?.default : {};
  }

  const { isClientResolved } = useContext(QueryContext);

  const onStoreChange = ({ output }) => {
    const { storeId: updatedStoreId } = output || {};
    if (!updatedStoreId) return;

    if (window && window.THD_LOCALIZER_AUTO_INIT && window.THD_LOCALIZER_AUTO_INIT.Localizer) {
      window.THD_LOCALIZER_AUTO_INIT.Localizer.localize({ storeId: updatedStoreId }).then(() => {
        window.location.reload();
      });
    }
  };

  useEffect(() => {
    const onResize = debounce(200, () => {
      if (window.innerWidth >= 1280) {
        setDiscoveryZoneContainerIndex({ zone1: 8, zone2: 16 });
      } else if (window.innerWidth >= 1024) {
        setDiscoveryZoneContainerIndex({ zone1: 6, zone2: 12 });
      } else if (window.innerWidth >= 640) {
        setDiscoveryZoneContainerIndex({ zone1: 4, zone2: 8 });
      }
    });
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', onResize);
    }

    onResize();
    setIsDiscoveryZonesLoaded(true);
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', onResize);
      }
    };
  }, []);

  useEffect(() => {
    window.LIFE_CYCLE_EVENT_BUS.on('product-results.change-store', onStoreChange);
    window.LIFE_CYCLE_EVENT_BUS.trigger('header.header-positioning-layout', { position: 'sticky' });
  }, []);

  const onDimensionsChange = useCallback(({ refinement }) => {
    if (!viewPrimaryFilters) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('filters-refinements.click', refinement);
    } else if (refinement?.dimensionName === 'Get It Fast') {
      storeLifecycleEvent('filters-refinements-visualNav.click', refinement);
    }
    navigate(refinement.url);
  });

  const resultsDimensionsRef = useRef(null);

  const onPageChange = useCallback(({ url }) => {
    navigate(url);

    const { offsetTop } = resultsDimensionsRef?.current?.parentElement?.parentElement;
    window.scrollTo(0, offsetTop || 0);
  });

  const onSortChange = useCallback(
    ({
      sortby, sortorder, name, currentPage, page, updatedPath = null
    }) => {
      const newPath = updateQueryParams(updatedPath || path, [
        {
          paramName: 'Nao',
          paramValue: null
        },
        {
          paramName: 'sortorder',
          paramValue: sortorder
        },
        {
          paramName: 'sortby',
          paramValue: sortby
        }
      ]);
      window.LIFE_CYCLE_EVENT_BUS.trigger('sort-refinements.click', {
        name,
        currentPage,
        page
      });
      navigate(newPath);
    }
  );

  const cleanPath = path.replace(/^\/render[^/]*/, '');

  const isEtchVisNavOnPLP = (data) => {
    try {
      const stringifiedPartialTemplates = data?.searchModel?.partialTemplates;
      const etchVisualNavExists = (stringifiedPartialTemplates?.[0] || '').indexOf('visualNavigation') > -1;
      const hasPLPBanner = data?.searchModel?.metadata?.hasPLPBanner;
      const isPLPPage = data?.searchModel?.metadata?.contentType === 'plppage';
      return hasPLPBanner && etchVisualNavExists && isPLPPage;
    } catch (err) {
      // eslint-disable-next-line no-console
      return false;
    }
  };

  const {
    catStyle,
    keyword,
    lowerbound,
    Nao = 0,
    nValue,
    sortby,
    sortorder,
    skipSpellCheck,
    upperbound
  } = parseUrl({ path: cleanPath });

  // For hybrid-contentful-plp
  const hybridParams = {
    slug: isNIncluded(nValue) ? nValue : 'N-' + nValue,
    customerType,
    instance,
    isShowProducts: catStyle === 'ShowProducts',
    storeId: store.storeId,
    skip: isSearch || isRefinedPage(pathname)
  };
  const { data: hybridData, contentElements, loading: hybridLoading } = useHybridPLP(hybridParams);
  useHelmet('hd-home', { hybridData }, imagePreloader, [hybridData]);
  const layouts = hybridData?.layouts;
  const layoutID = layouts?.id; // for impressions
  const isHybridLayout = layoutID && layouts?.content?.__typename === 'HybridPLPLayout'; // for validation
  const isBrandPage = layouts?.content?.pageType === 'Brand Page';

  const loadedStore = useRef(store);
  const localizedAndClientLoaded = useRef(false);

  const additionalSearchParams = {};
  const hasSponsoredSorts = !sortby || ['bestmatch', 'topsellers'].indexOf(sortby) > -1;

  if (cookie?.adobeCookie?.MCMID && searchNavPropsRef.current?.askForSponsored && hasSponsoredSorts) {
    additionalSearchParams.sponsored = true;
    additionalSearchParams.mcvisId = cookie.adobeCookie.MCMID;
  }

  const showProducts = /showproducts/i.test(catStyle);
  if (showProducts) {
    additionalSearchParams.plp = true;
  }

  if (skipSpellCheck) {
    additionalSearchParams.skipSpellCheck = skipSpellCheck;
  }

  if (searchNavPropsRef.current?.deliveryZip) {
    additionalSearchParams.deliveryZip = searchNavPropsRef.current.deliveryZip;
  }

  if (Object.keys(THDCustomer).length !== 0 && THDCustomer.svocID) {
    additionalSearchParams.svocID = THDCustomer.svocID;
  }

  const response = useProductResults({
    additionalSearchParams,
    keyword,
    lowerbound,
    nValue,
    pageSize: searchNavPropsRef.current?.pageSize,
    start: Nao,
    storeId: store.storeId,
    sortby,
    sortorder,
    upperbound,
    isCustomerIdentified: store.isLocalized ? isCustomerIdentified : false
  });

  const {
    data, error, loading, variables, responseLoading
  } = response;

  const isBuyitagainEnabled = useConfigService('fs-prop:isBuyitagainEnabled');

  const cleanedPath = (pathname || '').replace(/render-\w+\//, '');
  // check for when search doesn't change storeId response
  // it decides not to honor storeId change of localized store
  // because 8119 store return no results so its not gonna fetch again
  // however we need to update the storeId on this page so other components
  // that need localized store (RelatedSearch) can ask for it.
  if (!loading && store.storeId !== '8119' && variables.storeId === '8119') {
    localizedAndClientLoaded.current = true;
    loadedStore.current = {
      storeId: store.storeId,
      zipCode: variables.zipCode,
    };
  } else if (data && !loading && !response?.responseLoading && store.isLocalized) {
    localizedAndClientLoaded.current = true;
    loadedStore.current = {
      storeId: variables.storeId,
      zipCode: variables.zipCode
    };
  }

  const handleProductClick = useCallback((evt, product, action, sponsoredClickValues) => {
    let canonicalUrl = product?.identifiers?.canonicalUrl;
    if (canonicalUrl && !(evt.ctrlKey || evt.metaKey)) {
      evt.preventDefault();
      navigate(canonicalUrl);
    }
  }, []);

  const { products = [], dimensions = [], metadata } = data?.searchModel || {};
  const { contentType, stores } = metadata || {};
  const isCategory = contentType === 'categorypage';
  const isInStoreFilterON = hasInStoreFilter(data);
  const isEtchVisNavInPLP = isEtchVisNavOnPLP(data);

  const areAppliedRefinementsAvialable = (data?.searchModel?.appliedDimensions
    && data.searchModel.appliedDimensions.length > 0);

  const hasVisnavData = data?.searchModel?.relatedResults?.visualNavs?.length > 0;

  const mountedFn = ({ mounted }) => {
    if (!store.isLocalized) return false;
    if (response.responseLoading) return false;
    return mounted;
  };

  /*
  Get Sample Products
  */
  const {
    loading: samplesLoading
  } = usePLPSamples({
    products,
    productsFinishedLoading: searchNavPropsRef.current.pageSize === CLIENT_SIDE_PRODUCT_POD_COUNT
      && !response.responseLoading,
    storeId: store.storeId,
    storeZip: store.storeZip
  });
  // END: Get Sample Products

  const stringifyItemIds = (hookData) => {
    return hookData?.searchModel?.products?.map(({ itemId: sku }) => sku).join('-');
  };

  const stringifyAppliedRefinements = (hookData) => {
    const appliedDimensions = hookData?.searchModel?.appliedDimensions || [];
    return appliedDimensions
      .map(({ refinements = [] }) => {
        return refinements.map((refinement) => refinement.refinementKey).join('-');
      })
      .join('-');
  };
  const isClientSearchModelResolved = isClientResolved({ queryName: 'searchModel' });

  useEffect(() => {
    if (data && !loading && isClientSearchModelResolved) {
      const { stores: storeInfo, productCount = {} } = data?.searchModel?.metadata || {};

      const receivedAllProducts = store.storeId !== '8119' && !!storeInfo;
      const hasNoProducts = productCount?.allProducts === '0';
      const shouldTriggerBrowseAndCategoryAnalytics = isCategory
        || (pageType === 'browse' && (receivedAllProducts || hasNoProducts));

      const analyticsSearchModel = {
        ...data?.searchModel,
        defaultPageSize: searchNavPropsRef.current.pageSize,
        pageID: isNIncluded(nValue) ? nValue : 'N-' + nValue,
        isHybridContentful: isHybridLayout
      };
      if (shouldTriggerBrowseAndCategoryAnalytics) {
        LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('browse.new-products', analyticsSearchModel);
      } else if (pageType === 'search') {
        LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('search.new-products', analyticsSearchModel);
      }
    }
  }, [
    stringifyItemIds(data),
    stores?.storeId,
    stringifyAppliedRefinements(data),
    isClientSearchModelResolved
  ]);

  const decideFullWidth = isGetItFastOnPlp ? { xs: '12', sm: '12', md: '12', lg: '12' }
    : { xs: '8', sm: '9', md: '10', lg: '10' };

  const [resultsColProps, setResultsColProps] = useState(decideFullWidth);

  if (typeof window !== 'undefined') {
    window.LIFE_CYCLE_EVENT_BUS.on('LAYOUT_MANAGER.ADD_FS', (evt) => {
      if (evt.output['fs-disable:mf-dimensions__mf-results-dimensions'] === 'true') {
        setResultsColProps((prev) => {
          return { xs: '12' };
        });
      }
      if (evt.output['fs-disable:mf-dimensions__mf-results-dimensions'] === 'false') {
        setResultsColProps({
          xs: '8',
          sm: '9',
          md: '10',
          lg: '10'
        });
      }
    });
  }

  const skipFn = ({ skip, queryName, attributes }) => {
    if (queryName !== 'product') return skip;
    const isResolved = isClientResolved({ queryName: 'searchModel' });

    if (attributes.product.fulfillment) {
      if (isResolved) return skip;
      return true;
    }
    return skip;
  };

  const isDynamicBrandPage = data?.searchModel?.aisles?.length > 0;
  isNoResultsFoundRef.current = pageType === 'search'
    && data?.searchModel?.searchReport?.totalProducts === 0 && !!keyword;

  const maxNumOfPodsToHydrate = 12;
  const hasCategoryCard = data?.searchModel?.orangeGraph?.universalSearchArray.length > 0;
  useEffect(() => {
    const onResize = debounce(200, () => {
      if (isClientSearchModelResolved) {
        // 4 pod layout
        if (window.innerWidth >= 1280) {
          if (hasCategoryCard) {
            if (products?.length >= 24) {
              setInGridBannerIndex(24);
              setIsInGridBannerBottom(false);
            } else if (products?.length < 24) {
              setIsInGridBannerBottom(true);
              setInGridBannerIndex(null);
            }
          }
          if (!hasCategoryCard) {
            if (products?.length >= 12) {
              setInGridBannerIndex(12);
              setIsInGridBannerBottom(false);
            } else {
              setIsInGridBannerBottom(true);
              setInGridBannerIndex(null);
            }
          }
          // 3 pod layout
        } else if (window.innerWidth >= 1024) {
          if (hasCategoryCard) {
            if (products?.length >= 18) {
              setInGridBannerIndex(18);
              setIsInGridBannerBottom(false);
            } else {
              setIsInGridBannerBottom(true);
              setInGridBannerIndex(null);
            }
          }
          if (!hasCategoryCard) {
            if (products?.length >= 9) {
              setInGridBannerIndex(9);
              setIsInGridBannerBottom(false);
            } else {
              setIsInGridBannerBottom(true);
              setInGridBannerIndex(null);
            }
          }
        }
      }
    });
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', onResize);
    }

    onResize();

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', onResize);
      }
    };
  }, [isClientSearchModelResolved]);

  const productsReduce = (productPods) => {
    let groupOfPods = 0;

    let reduced = productPods?.reduce((hydrator, product) => {
      if (!hydrator[groupOfPods]) {
        hydrator.push([]);
      }
      if (hydrator[groupOfPods].length < maxNumOfPodsToHydrate) {
        hydrator[groupOfPods].push(product);
        if (hydrator[groupOfPods].length === maxNumOfPodsToHydrate) {
          groupOfPods += 1;
        }
      }
      return hydrator;
    }, []);

    if (productPods.length === SERVER_SIDE_PRODUCT_POD_COUNT && !isClientSearchModelResolved) {
      const placeholderHydrators = (CLIENT_SIDE_PRODUCT_POD_COUNT
        - SERVER_SIDE_PRODUCT_POD_COUNT) / maxNumOfPodsToHydrate;
      for (let i = 0; i < placeholderHydrators; i += 1) {
        reduced.push(Array(maxNumOfPodsToHydrate).fill());
      }
    }

    return reduced;
  };

  const [selectedForCompare, setSelectedForCompare] = useState([]);

  const onSelectedForCompare = (id, selected = false) => {
    if (selected) {
      setSelectedForCompare([...selectedForCompare, id]);
    } else {
      setSelectedForCompare(selectedForCompare.filter((item) => item !== id));
    }
  };

  const onCompare = () => {
    let queryparams = selectedForCompare.map((item, idx) => `item${idx + 1}=${item}`).join('&');

    queryparams += hasInStoreFilter(data) ? '&inStoreFilter=true' : '';
    if (cleanedPath) {
      window.sessionStorage.setItem('compareBaseURL', cleanedPath);
    }

    navigate(`/compare?${queryparams}`, { state: { bypassExperienceCheck: true } });
  };

  useEffect(() => {
    const itemIds = window.sessionStorage.getItem('compare');
    const compareBaseURL = window.sessionStorage.getItem('compareBaseURL');
    window.sessionStorage.removeItem('compareBaseURL');
    window.sessionStorage.removeItem('compare');
    if (itemIds && compareBaseURL === cleanedPath) {
      setSelectedForCompare(JSON.parse(itemIds));
    }
  }, []);

  const originalProducts = useRef(productsReduce(products));
  const hydrators = isClientSearchModelResolved || !originalProducts.current?.length
    ? productsReduce(products)
    : originalProducts.current;

  if (isNoResultsFoundRef.current) {
    return (
      <NoResultsFound
        keyword={keyword}
        requestKey={requestKey}
        searchData={data}
        searchError={error}
        searchLoading={loading}
        storeId={loadedStore.current?.storeId}
        isCategory={isCategory}
        pageType={pageType}
      />
    );
  }

  const hasDeliveryFilter = (breadCrumbs, filterLabelList) => {
    return (breadCrumbs || []).filter((breadCrumbDetail) => filterLabelList.includes(breadCrumbDetail?.label));
  };

  const applicableFilters = ['Same-Day Delivery', 'Next-Day Delivery', 'Free 1-2 Day Delivery'];
  const hasSameDayFilter = !!(hasDeliveryFilter(data?.searchModel?.taxonomy?.breadCrumbs, applicableFilters).length);

  return (
    <>
      <BrowseSearchRedirector variables={response?.variables} />
      <Row className="isBound browse-search">
        <ErrorBoundary name="browse-and-search-metadata">
          <Metadata>
            <BrowseSearchMetadata
              data={data}
              loading={loading}
              error={error}
              variables={response?.variables}
              seoGeneratedContentVariables={{ navParam: response?.variables.navParam }}
              pathWithQueryParams={path}
            />
          </Metadata>
        </ErrorBoundary>
        <Row className="u--paddingTop u--paddingBottom">
          <ErrorBoundary name="breadcrumbs">
            <Col flatten>
              <div className={isHybridLayout && 'sui-ml-2'}>
                <Breadcrumbs
                  data={data}
                  error={error}
                  loading={loading}
                />
              </div>
            </Col>
          </ErrorBoundary>
        </Row>
        <Row>
          <ErrorBoundary name="product-results-group">
            <Col flatten>
              <ProductResults data={data} error={error} loading={loading}>
                <ErrorBoundary name="sponsored-banner-nucleus">
                  <Row>
                    <Col>
                      <div className={isHybridLayout && 'sui-ml-2'}>
                        <SponsoredBanner
                          browserId={thdCustomer?.mcvisID || ''}
                          pageContext={{
                            label: 'browse-search',
                            data,
                            keyword,
                            isCategory,
                            isBrowse,
                            isSearch
                          }}
                          avoidPreload
                        />
                      </div>
                    </Col>
                  </Row>
                </ErrorBoundary>
                <Row>
                  <ErrorBoundary name="results-header">
                    <Col flatten>
                      <div className={isHybridLayout && 'sui-ml-2'}>
                        <ResultsHeader
                          resultsRef={resultsDimensionsRef}
                          showShopAllLink={isDynamicBrandPage}
                        />
                      </div>
                    </Col>
                  </ErrorBoundary>
                </Row>
                {!isHybridLayout && !isRefinedPage(pathname) && (
                  <Row className="edgewidgets-row-container">
                    <ErrorBoundary name="etch-widgets">
                      <Col nopadding>
                        <EtchWidgets
                          data={data}
                          dpdNewTab
                          error={error}
                          exclude={['plpSeo']}
                          keyword={keyword}
                          loading={loading}
                          navParam={nValue}
                          showFavoritesCount
                          storeId={loadedStore.current?.storeId}
                          isCategory={isCategory}
                          isBrowse={isBrowse}
                          isSearch={isSearch}
                        />
                      </Col>
                    </ErrorBoundary>
                  </Row>
                )}
                {isHybridLayout && !hybridLoading && isBrowse && (
                  <ErrorBoundary name="contentful-widgets">
                    <HybridContentfulWidgets
                      contentElements={contentElements}
                      loading={hybridLoading}
                      isBrowse={isBrowse}
                    />
                  </ErrorBoundary>
                )}
                {!isEtchVisNavInPLP && viewVizNavFilters && !isBrandPage && hasVisnavData && (
                  <Row name="visual-nav">
                    <div
                      className={isHybridLayout ? 'sui-ml-2 sui-mr-2 sui-w-full' : 'sui-w-full'}
                    >
                      <ErrorBoundary>
                        <Col className="visual-nav-container">
                          <VisualCategoryNavigation
                            data={data}
                          />
                        </Col>
                      </ErrorBoundary>
                    </div>
                  </Row>
                )}
                {!isCategory
                  && shouldDisplayResultsAppliedOuter(ctx)
                  && !viewPrimaryFilters && (
                  <Row>
                    <ErrorBoundary name="results-applied">
                      <ErrorBoundary name="top-results-applied">
                        <Col sm="9">
                          <ResultsApplied
                            onAppliedChange={onDimensionsChange}
                            isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                          />
                        </Col>
                      </ErrorBoundary>
                      <Col xs="3">
                        <ResultsSortBy className="sui-flex sui-justify-end" onSortChange={onSortChange} />
                      </Col>
                    </ErrorBoundary>
                  </Row>
                )}

                {!isCategory && (
                  <Row>
                    <ErrorBoundary
                      name="results-dimensions"
                      className="browse-search__left-outer-padding"
                    >
                      { !isGetItFastOnPlp && (
                        <ErrorBoundary name="mf-results-dimensions">
                          <Col lg="2" md="2" sm="3" xs="4" flatten>
                            {!hideFFMFilters && (
                              <ResultsDimensions
                                enableMultiStore
                                enableVisualRefinement
                                onDimensionsChange={onDimensionsChange}
                                shiftCategoryDimension={isDynamicBrandPage}
                                showOnlyGetItFast={viewAllFilterButton && viewPrimaryFilters}
                                isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                              />
                            )}
                            {!isInGridBannerEnabled && (
                              <ErrorBoundary name="sponsored-skyscraper-banner">
                                <SponsoredSkyscraperBanner
                                  pageContext={{
                                    label: 'browse-search',
                                    data,
                                    keyword,
                                    isCategory,
                                    isBrowse,
                                    isSearch
                                  }}
                                  dynamic={{
                                    pageType
                                  }}
                                  hydrator={{
                                    className: 'grid',
                                    scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
                                  }}
                                  browserId={thdCustomer?.mcvisID || ''}
                                />
                              </ErrorBoundary>
                            )}
                          </Col>
                        </ErrorBoundary>
                      )}
                      {
                        isBuyitagainEnabled && (
                          <ErrorBoundary>
                            <div className="sui-grid sui-w-full sui-ml-2">
                              <BuyItAgain
                                nValue={nValue}
                                schemaName="buyitagain"
                                anchorId={`N-${nValue}`}
                                loading={responseLoading}
                                data={data}
                                error={error}
                                showLoading
                              />
                            </div>
                          </ErrorBoundary>
                        )
                      }
                      <Col
                        className="browse-search__pod-col-no-padding"
                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                        {...resultsColProps}
                      >
                        {isGetItFastOnPlp && (
                          <Row className="browse-search--no-left-margin">
                            <ErrorBoundary name="top-results-applied">
                              <Col sm="9">
                                <ResultsApplied
                                  onAppliedChange={onDimensionsChange}
                                  viewPrimaryFilters={viewPrimaryFilters}
                                  isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                                  isGetItFastOnPlp={isGetItFastOnPlp}
                                />
                              </Col>
                            </ErrorBoundary>
                          </Row>
                        )}
                        {viewPrimaryFilters && (
                          <>
                            <ErrorBoundary name="filter-and-sort-drawer">
                              <ResultsFilterAndSortDrawer
                                onSortChange={onSortChange}
                                onDimensionsChange={onDimensionsChange}
                                disableStickyFilter
                                excludePickUpToday
                                hideViewAll={!viewAllFilterButton}
                              />
                            </ErrorBoundary>
                          </>
                        )}
                        {(viewPrimaryFilters) && (
                          <>
                            <>
                              {isGetItFastOnPlp && (
                                <ErrorBoundary name="results-applied">
                                  <Row className="browse-search--no-left-margin sui-mt-3">
                                    {!hideFFMFilters && (
                                      <Col xs="9">
                                        <ErrorBoundary name="results-get-it-fast">
                                          <ResultsDimensions
                                            onDimensionsChange={onDimensionsChange}
                                            showOnlyGetItFast
                                            isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                                            isGetItFastOnPlp={isGetItFastOnPlp}
                                            viewPrimaryFilters={viewPrimaryFilters}
                                          />
                                        </ErrorBoundary>
                                      </Col>
                                    )}
                                    <Col xs={hideFFMFilters ? '12' : '3'}>
                                      <ResultsSortBy className="sui-flex sui-justify-end" onSortChange={onSortChange} />
                                    </Col>
                                  </Row>
                                </ErrorBoundary>
                              )}
                              {areAppliedRefinementsAvialable && (
                                <ErrorBoundary name="top-results-applied">
                                  <Col className="sui-pb-5 sui-pt-0">
                                    <ResultsApplied
                                      onAppliedChange={onDimensionsChange}
                                      hideResultsCount={viewPrimaryFilters}
                                      isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                                      isGetItFastOnPlp={isGetItFastOnPlp}
                                    />
                                  </Col>
                                </ErrorBoundary>
                              )}
                            </>
                            {!isGetItFastOnPlp && (
                              <Row className="browse-search--no-left-margin">
                                <ErrorBoundary name="results-applied">
                                  <ErrorBoundary name="top-results-applied">
                                    <Col sm="9">
                                      <ResultsApplied
                                        onAppliedChange={onDimensionsChange}
                                        viewPrimaryFilters={viewPrimaryFilters}
                                        isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                                      />
                                    </Col>
                                  </ErrorBoundary>
                                  <Col xs="3">
                                    <ResultsSortBy onSortChange={onSortChange} />
                                  </Col>
                                </ErrorBoundary>
                              </Row>
                            )}
                          </>
                        )}
                        <Row className="browse-search--no-left-margin">
                          {shouldDisplayResultsAppliedInner(ctx) && (
                            <ErrorBoundary name="results-applied">
                              <Col flatten>
                                <Col sm="9">
                                  <ResultsApplied
                                    onAppliedChange={onDimensionsChange}
                                    isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                                  />
                                </Col>
                                <Col xs="3">
                                  <ResultsSortBy onSortChange={onSortChange} />
                                </Col>
                              </Col>
                            </ErrorBoundary>
                          )}
                          <ErrorBoundary name="wrapped-results">
                            <ImpressionProvider
                              data={impProductPodData(layoutID)}
                            >
                              <ResultsWrapped>
                                <div className="sui-grid sui-pl-3 sui-w-full sui-mt-2" ref={resultsDimensionsRef}>
                                  {!(products || []).length && loading && pageType === 'search' && (
                                    <ProductPodPlaceholder
                                      podsCount={SERVER_SIDE_PRODUCT_POD_COUNT}
                                    />
                                  )}
                                  {!!hydrators?.length && (
                                    <QueryProvider
                                      cacheKey="search-product-pods-desktop"
                                      dataSource="searchNav"
                                      defaultVariables={{
                                        storeId: loadedStore.current?.storeId,
                                        skipKPF: true,
                                        skipSubscribeAndSave: true,
                                        skipInstallServices: false,
                                        isBrandPricingPolicyCompliant: localizedAndClientLoaded.current
                                          ? isCustomerIdentified : false,
                                      }}
                                      mounted={mountedFn}
                                      skip={skipFn}
                                    >
                                      {hydrators?.map((group, hydratorIndex) => {
                                        if (!group?.length) return null;
                                        return (
                                          <Hydrator
                                            immediateRender={!hydratorIndex}
                                            className="sui-grid sm:sui-grid-cols-2
                                          lg:sui-grid-cols-3 xl:sui-grid-cols-4 sui-gap-2 sui-mb-2"
                                            id={`browse-search-pods-${hydratorIndex + 1}`}
                                            key={`hydrator-${hydratorIndex}`}
                                            patch={!hydratorIndex}
                                            scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                                          >
                                            <HoistedProductPodGroup
                                              DetailsPod={CustomProductPodDetails}
                                              keyword={keyword}
                                            >
                                              {group.map((product, itemIndex, productArray) => {
                                                const { itemId, canonicalUrl } = product?.identifiers || {};
                                                podIndex += 1;
                                                if (!itemId) {
                                                  return (
                                                    <div
                                                      className="sui-grid"
                                                      key={`placeholder-${itemIndex}`}
                                                    >
                                                      <RectShape
                                                        color="#E0E0E0"
                                                        style={{
                                                          width: '100%',
                                                          height: '250px',
                                                          marginBottom: '10px'
                                                        }}
                                                      />
                                                      <TextBlock color="#E0E0E0" rows={7} />
                                                    </div>
                                                  );
                                                }
                                                const showContentCard = hydratorIndex * maxNumOfPodsToHydrate
                                                  + itemIndex
                                                  === SERVER_SIDE_PRODUCT_POD_COUNT;

                                                const showInGridBanner = (
                                                  (hydratorIndex * maxNumOfPodsToHydrate)
                                                  + itemIndex === inGridBannerIndex
                                                );

                                                const podPosition = hydratorIndex * maxNumOfPodsToHydrate + itemIndex;

                                                const showDiscoveryZone1 = (
                                                  ((hydratorIndex * maxNumOfPodsToHydrate) + itemIndex
                                                    === discoveryZoneContainerIndex.zone1)
                                                );
                                                const showDiscoveryZone2 = (
                                                  ((hydratorIndex * maxNumOfPodsToHydrate) + itemIndex
                                                    === discoveryZoneContainerIndex.zone2)
                                                );

                                                return (
                                                  <ErrorBoundary
                                                    id={`plp-product-pod__${itemIndex}`}
                                                    index={itemIndex}
                                                    key={`${itemId}-${itemIndex}`}
                                                    name={`Product Pod: ${itemIndex}`}
                                                  >
                                                    {showContentCard
                                                      && (
                                                        <div className="sm:sui-col-span-2
                                          lg:sui-col-span-3 xl:sui-col-span-4"
                                                        >
                                                          <CategoryCardsDesktop
                                                            data={data}
                                                            error={error}
                                                            loading={loading}
                                                            storeId={loadedStore.current?.storeId}
                                                            keyword={keyword}
                                                            navParam={nValue}
                                                          />
                                                        </div>
                                                      )}
                                                    {showInGridBanner
                                                      && isClientSearchModelResolved
                                                      && isInGridBannerEnabled
                                                      && !isInGridBannerBottom
                                                      && (
                                                        <div className="sm:sui-col-span-2
                                          lg:sui-col-span-3 xl:sui-col-span-4"
                                                        >
                                                          <ErrorBoundary name="sponsored-ingrid-nucleus">
                                                            <SponsoredInGridBanner
                                                              browserId={thdCustomer?.mcvisID || ''}
                                                              pageContext={{
                                                                label: 'browse-search',
                                                                data,
                                                                keyword,
                                                                isCategory,
                                                                isBrowse,
                                                                isSearch
                                                              }}
                                                            />
                                                          </ErrorBoundary>
                                                        </div>
                                                      )}
                                                    {isDiscoveryZonesEligible
                                                      && showDiscoveryZone1
                                                      && (
                                                        <div className="sm:sui-col-span-2
                                          lg:sui-col-span-3 xl:sui-col-span-4 sui-pb-2"
                                                        >
                                                          <DiscoveryZonesWrapper
                                                            data={data}
                                                            loading={loading}
                                                            nValue={nValue}
                                                            schemaName="discoveryZones"
                                                            storeId={loadedStore.current?.storeId}
                                                            zoneId="1"
                                                            errorBoundary
                                                            hydrator={{
                                                              className: 'sui-bg-primary sui-grid',
                                                              delay: 1500,
                                                              scrollBuffer: 500,
                                                              id: 'discovery-zones-1',
                                                              immediateRender: isDiscoveryZonesLoaded
                                                            }}
                                                            dynamic={{
                                                              pageType
                                                            }}
                                                          />
                                                        </div>
                                                      )}

                                                    {((isBrowse && isDiscoveryZonesEnabled)
                                                        || (isSearch && isDiscoveryZonesSearchEnabled))
                                                      && showDiscoveryZone2 && (
                                                      <div className="sm:sui-col-span-2
                                          lg:sui-col-span-3 xl:sui-col-span-4"
                                                      >
                                                        <DiscoveryZonesWrapper
                                                          data={data}
                                                          loading={loading}
                                                          nValue={nValue}
                                                          schemaName="discoveryZones"
                                                          storeId={loadedStore.current?.storeId}
                                                          zoneId="2"
                                                          errorBoundary
                                                          hydrator={{
                                                            className: 'sui-grid',
                                                            delay: 1500,
                                                            scrollBuffer: 500,
                                                            id: 'discovery-zones-2',
                                                          }}
                                                          dynamic={{
                                                            pageType
                                                          }}
                                                        />
                                                      </div>
                                                    )}
                                                    <div className="sui-grid">
                                                      <CustomProductPodResponsive
                                                        parent="plp"
                                                        itemId={itemId}
                                                        position={podPosition}
                                                        onClick={handleProductClick}
                                                        storeId={loadedStore.current?.storeId}
                                                        samplesLoading={samplesLoading}
                                                        impProductPodData={impProductPodData(layoutID)}
                                                        hasSameDayFilter={hasSameDayFilter}
                                                        url={canonicalUrl}
                                                        hasInStoreFilter={isInStoreFilterON}
                                                        hoverDelay={productPodHoverDelay}
                                                        onCompare={onCompare}
                                                        onSelectedForCompare={onSelectedForCompare}
                                                        selectedForCompare={selectedForCompare}
                                                        isFulfillmentFilterAndMsgEnabled={isFulfillmentFSEnabled}
                                                      />
                                                    </div>
                                                  </ErrorBoundary>
                                                );
                                              })}
                                            </HoistedProductPodGroup>
                                          </Hydrator>
                                        );
                                      })}
                                    </QueryProvider>
                                  )}
                                </div>
                              </ResultsWrapped>
                            </ImpressionProvider>
                          </ErrorBoundary>
                          <DynamicComponent pageType={pageType}>
                            <Hydrator id="category-cards">
                              <ErrorBoundary>
                                {products?.length < SERVER_SIDE_PRODUCT_POD_COUNT && (
                                  <CategoryCardsDesktop
                                    data={data}
                                    error={error}
                                    loading={loading}
                                    storeId={loadedStore.current?.storeId}
                                    keyword={keyword}
                                    navParam={nValue}
                                  />
                                )}
                              </ErrorBoundary>
                              {isClientSearchModelResolved
                                && isInGridBannerEnabled
                                && isInGridBannerBottom && (
                                <Col className="sui-pr-0">
                                  <ErrorBoundary name="sponsored-ingrid-nucleus">
                                    <SponsoredInGridBanner
                                      browserId={thdCustomer?.mcvisID || ''}
                                      pageContext={{
                                        label: 'browse-search',
                                        data,
                                        keyword,
                                        isCategory,
                                        isBrowse,
                                        isSearch
                                      }}
                                    />
                                  </ErrorBoundary>
                                </Col>
                              )}
                            </Hydrator>
                            <Row className="u--paddingTop u--paddingBottom">
                              <ErrorBoundary name="pagination">
                                <Col>
                                  <ResultsPagination
                                    onPageChange={onPageChange}
                                    pageSize={searchNavPropsRef.current?.pageSize}
                                    path={path}
                                  />
                                </Col>
                              </ErrorBoundary>
                            </Row>
                            {!hideFeedback && (
                              <Hydrator id="search-feedback" className="sui-grid sui-w-full">
                                <div>
                                  <ErrorBoundary name="search-feedback">
                                    <div className="sui-py-3 sui-pl-3">
                                      <ImpressionProvider
                                        key="SearchFeedback"
                                        data={{
                                          id: '',
                                          component: 'SearchFeedback',
                                          name: 'SearchFeedback',
                                          type: 'content'
                                        }}
                                      >
                                        <SearchFeedback />
                                      </ImpressionProvider>
                                    </div>
                                  </ErrorBoundary>
                                </div>
                              </Hydrator>
                            )}
                          </DynamicComponent>
                          <Row className="u--paddingTop u--paddingBottom">
                            <ErrorBoundary name="compare">
                              <Col>
                                <CompareTray
                                  itemIds={selectedForCompare}
                                  onRemove={onSelectedForCompare}
                                  onCompare={onCompare}
                                />
                              </Col>
                            </ErrorBoundary>
                          </Row>
                        </Row>
                      </Col>
                    </ErrorBoundary>
                  </Row>
                )}
              </ProductResults>
            </Col>
          </ErrorBoundary>
        </Row>
        {!hideEmailPromo && (
          <DynamicComponent pageType={pageType}>
            <Hydrator id="email-promo" className="grid">
              <Row className="u--paddingTop u--paddingBottom">
                <ErrorBoundary name="email-promo">
                  <Col>
                    <HdhomeSignup
                      formSubmission
                      subscriptionType={subscriptionType}
                      campaignId={browseCampaignId}
                    />
                  </Col>
                </ErrorBoundary>
              </Row>
            </Hydrator>
          </DynamicComponent>
        )}
        {!hybridLoading && !isBrandPage && (
          <RetailMediaCarousel
            errorBoundary
            name="sponsored-carousel-nucleus"
            breadCrumbs={data?.searchModel?.taxonomy?.breadCrumbs}
            categorySourceId={data?.searchModel?.metadata?.categoryID}
            nValue={nValue}
            pageContext={{
              label: 'browse-search',
              data: {
                itemIds: getBrowseAndSearchItemIds(products)
              },
              keyword,
              isCategory,
              isBrowse,
              isSearch
            }}
            showSponsoredCarousel
            dynamic={{
              pageType
            }}
            hydrator={{
              className: 'grid zone-card',
              delay: 1500,
              scrollBuffer: CAROUSEL_HYDRATION_STANDARD_SCROLL_BUFFER
            }}
          />
        )}
        {!hybridLoading && !isBrandPage && (
          <PlpRecsRenderPlaceholder
            requestKey={requestKey}
            isBrowse={isBrowse}
            keyword={keyword}
            itemIds={getBrowseAndSearchItemIds(products)}
            impressionData={impPLPRecs()}
            browseNValue={filterBrowseRecs ? nValue : ''}
          />
        )}
        {!hybridLoading && (
          <PlpRvRenderPlaceholder isCategory={isCategory} isSearch={isSearch} />
        )}
        {!isSearch && !hybridLoading && (
          <>
            <DynamicComponent pageType={pageType}>
              <Hydrator
                className="grid"
                id="browse-emt-section"
                delay={1500}
                placeholder={(
                  <LoadingPlaceholder
                    color="#f5f5f5"
                    ready={false}
                    style={{ height: 750 }}
                    type="rect"
                  >
                    <div />
                  </LoadingPlaceholder>
                )}
                waitFor={[EmtGeneratedContentLoad, EmtLinksLoad]}
                scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
              >
                <Row>
                  <ErrorBoundary name="emt-product-category">
                    <Col>
                      <EmtGeneratedContent type="productCategoryContent" nvalue={nValue} />
                    </Col>
                  </ErrorBoundary>
                </Row>
                <Row>
                  <ErrorBoundary name="etch-seo">
                    <Col>
                      <EtchWidgets
                        data={data}
                        error={error}
                        include={['plpSeo']}
                        loading={loading}
                        navParam={`N-${nValue}`}
                      />
                    </Col>
                  </ErrorBoundary>
                </Row>
              </Hydrator>
            </DynamicComponent>

            <ErrorBoundary name="emt-questions-and-answers">
              <EmtQuestionsAndAnswersImporter
                nValue={nValue}
                dynamic={{
                  pageType
                }}
                hydrator={{
                  className: 'sui-px-2 2xl:sui-px-0 sui-my-4'
                }}
              />
            </ErrorBoundary>
          </>
        )}
        {!isSearch && !hybridLoading && (
          <DynamicComponent pageType={pageType}>
            <Hydrator
              className="grid"
              id="emt-related-categories"
              delay={1500}
              placeholder={(
                <LoadingPlaceholder
                  color="#f5f5f5"
                  ready={false}
                  style={{ height: 350 }}
                  type="rect"
                >
                  <div />
                </LoadingPlaceholder>
              )}
              waitFor={[EmtGeneratedContentLoad]}
              scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
            >
              <Col>
                <EmtGeneratedContent type="relatedCategories" nvalue={`N-${nValue}`} nopadding />
              </Col>
            </Hydrator>
          </DynamicComponent>
        )}
        <DynamicComponent pageType={pageType}>
          <Hydrator
            className="grid"
            id="search-emt-section"
            delay={500}
            placeholder={(
              <LoadingPlaceholder color="#f5f5f5" ready={false} style={{ height: 750 }} type="rect">
                <div />
              </LoadingPlaceholder>
            )}
            scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
          >
            <Row className="u--paddingTop u--paddingBottom">
              <ErrorBoundary name="related-search">
                <Col
                  // eslint-disable-next-line react/jsx-boolean-value
                  suppressHydrationWarning={true}
                >
                  <RelatedSearch
                    navParam={nValue}
                    searchData={data}
                    searchError={error}
                    searchLoading={loading}
                    storeId={loadedStore.current?.storeId}
                  />
                </Col>
              </ErrorBoundary>
            </Row>
          </Hydrator>
        </DynamicComponent>
        {!isSearch && (
          <DynamicComponent pageType={pageType}>
            <Hydrator
              className="grid"
              id="browse-emt-links"
              delay={1500}
              placeholder={(
                <LoadingPlaceholder
                  color="#f5f5f5"
                  ready={false}
                  style={{ height: 300 }}
                  type="rect"
                >
                  <div />
                </LoadingPlaceholder>
              )}
              waitFor={[EmtGeneratedContentLoad, EmtLinksLoad]}
              scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
            >
              <Row className="u--paddingTop u--paddingBottom">
                <ErrorBoundary name="emt-mmg">
                  <Col nopadding>
                    <EmtLinks path={cleanedPath} />
                  </Col>
                </ErrorBoundary>
              </Row>
            </Hydrator>
          </DynamicComponent>
        )}
      </Row>
    </>
  );
};

BrowseAndSearch.displayName = 'BrowseAndSearchPage';

BrowseAndSearch.themeProps = {
  hideFFMFilters: bool,
  hideEmailPromo: bool,
  hideFeedback: bool,
};

BrowseAndSearch.defaultThemeProps = {
  hideFFMFilters: false,
  hideEmailPromo: false,
  hideFeedback: false,
};

BrowseAndSearch.propTypes = {
  pageType: string.isRequired,
  productPodHoverDelay: number,
  pathname: string.isRequired,
  customerType: string.isRequired,
  instance: shape({}).isRequired
};

BrowseAndSearch.defaultProps = {
  productPodHoverDelay: 500
};

const ProductSearchDataModel = {
  searchModel: params({ keyword: stringType(), navParam: stringType() }).shape({
    products: params({ startIndex: numberType() }).arrayOf(CustomProductPodResponsive.dataModel.product)
  })
};

BrowseAndSearch.dataModel = extend(
  {
    searchModel: params().shape({
      metadata: shapeType({
        hasPLPBanner: boolType(),
        categoryID: stringType(),
        analytics: shapeType({
          semanticTokens: stringType(),
          dynamicLCA: stringType()
        })
      })
    })
  },
  ResultsDimensions,
  ResultsFilterAndSortDrawer,
  BrowseSearchRedirector,
  BrowseSearchMetadata,
  Breadcrumbs,
  DiscoveryZonesWrapper,
  EtchWidgets,
  VisualCategoryNavigation,
  CategoryCardsDesktop,
  CompareTray,
  HdhomeSignup,
  EmtGeneratedContentModel,
  EmtQuestionsAndAnswersImporter,
  EmtLinksModel,
  RelatedSearch,
  OrderSample,
  NoResultsFound,
  productResultsDataModel,
  PlpRecsRenderPlaceholder,
  PlpRvRenderPlaceholder,
  BuyItAgain,
  SponsoredBanner,
  HybridContentfulWidgets.dataModel,
  // This must come last
  ProductSearchDataModel
);
